import { FRAC_LAYER, RIG_LAYER } from "constants/mapLayers.constants";

import useBetaFeatures from "hooks/useBetaFeatures";

import { useUpdateMapLayersVisibility } from "components/map/hooks";
import {
  DEFAULT_CHECKED_LAYERS,
  DEFAULT_EXPANDED_TREE_KEYS
} from "components/project/layers/constants/projectLayerConstants";
import { useProjectDispatch } from "components/project/projects/context";

import {
  getCheckedLayersFromSession,
  getCheckedTreeKeysFromSession,
  setCheckedLayersToSession,
  setCheckedTreeKeysToSession
} from "../../session";
import { getLayerTreeExpandedKeysFromSession } from "../../session/layer-tree-expanded-keys/getLayerTreeExpandedKeysFromSession";
import { setLayerTreeExpandedKeysToSession } from "../../session/layer-tree-expanded-keys/setLayerTreeExpandedKeysToSession";

interface ClearLayersOptions {
  // When true, ignore any per-project session selection and force the default
  // checked layers (used when deleting a view — see below).
  resetToDefaults?: boolean;
}

export function useClearLayers() {
  const projectDispatch = useProjectDispatch();
  const { updateMapLayersVisibility } = useUpdateMapLayersVisibility();
  const { hasFeature } = useBetaFeatures();

  function clearLayers(nextProjectId: string, options: ClearLayersOptions = {}) {
    // Default checked groups are Well, Facility, Completion, Grid, and Base,
    // plus Rig/Frac when the "Rig And Frac Layers" beta flag is on (its tree
    // group only exists in that case).
    const defaultCheckedLayers = hasFeature("Rig And Frac Layers")
      ? [...DEFAULT_CHECKED_LAYERS, RIG_LAYER, FRAC_LAYER]
      : DEFAULT_CHECKED_LAYERS;

    // Deleting a view must snap the tree back to the defaults, NOT restore the
    // last session selection. On every other path (initial load, project
    // switch) we keep restoring the session so the user's layer set survives.
    const checkedLayerKeys = options.resetToDefaults
      ? defaultCheckedLayers
      : getCheckedLayersFromSession(nextProjectId) || defaultCheckedLayers;

    const checkedTreeKeys = options.resetToDefaults
      ? defaultCheckedLayers
      : getCheckedTreeKeysFromSession(nextProjectId) || defaultCheckedLayers;

    const layerTreeExpandedKeys = options.resetToDefaults
      ? DEFAULT_EXPANDED_TREE_KEYS
      : getLayerTreeExpandedKeysFromSession(nextProjectId) || DEFAULT_EXPANDED_TREE_KEYS;

    // Persist the defaults over the stale session selection so the reset sticks
    // (otherwise the old selection would resurface on the next project switch).
    if (options.resetToDefaults && nextProjectId) {
      setCheckedLayersToSession({ checkedLayerKeys, projectId: nextProjectId });
      setCheckedTreeKeysToSession({ checkedTreeKeys, projectId: nextProjectId });
      setLayerTreeExpandedKeysToSession({
        expandedKeys: layerTreeExpandedKeys,
        projectId: nextProjectId
      });
    }

    projectDispatch({
      payload: {
        checkedLayerKeys,
        checkedTreeKeys,
        layerTreeExpandedKeys
      }
    });

    updateMapLayersVisibility(checkedLayerKeys, nextProjectId);
  }

  return { update: clearLayers };
}
